/** biome-ignore-all lint/style/useNamingConvention: External API payloads */
import type { Plan } from '@/app_lib/types/Plan';

interface PianoResource {
  name: string;
  rid: string;
}

interface PianoCustomField {
  fieldName: string;
  value: string;
}

interface TextCustomField extends PianoCustomField {
  options: string[];
  dataType: 'TEXT';
}

interface CheckboxCustomField extends PianoCustomField {
  options: string[];
  dataType: 'BOOLEAN';
}

interface SingleSelectCustomField extends PianoCustomField {
  options: string[];
  dataType: 'SINGLE_SELECT_LIST';
}

interface RenewalInvoice {
  amount: number;
  date: string;
}

export type PianoCustomFieldData = (TextCustomField | SingleSelectCustomField | CheckboxCustomField)[];

export enum MyAccountPages {
  Home = 'home',
  Profile = 'profile',
  Billing = 'billing',
  Subscriptions = 'subscriptions',
  Newsletters = 'newsletters',
}

export const MyAccountPageTitles = {
  [MyAccountPages.Home]: 'My profile',
  [MyAccountPages.Profile]: 'My profile',
  [MyAccountPages.Billing]: 'Billing',
  [MyAccountPages.Subscriptions]: 'Subscription overview',
  [MyAccountPages.Newsletters]: 'Newsletters',
};

export type UserAddress = {
  name: string;
  street1: string;
  street2?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
} | null;

export const pianoCustomFieldMap = {
  country: 'country',
  'experience-level': 'experienceLevel',
  industry: 'industry',
  'job-function': 'jobFunction',
  salutation: 'prefix',
};

export const reversePianoCustomFieldMap = Object.fromEntries(
  Object.entries(pianoCustomFieldMap).map(([key, value]) => [value, key]),
);

export interface PianoCustomFields {
  country: string;
  'experience-level': string;
  industry: string;
  'job-function': string;
  salutation: string;
}

export interface ProfileTextFields {
  email: string;
  firstName: string;
  lastName: string;
}

export interface ProfileSelectFields {
  country: string;
  experienceLevel: string;
  industry: string;
  jobFunction: string;
  prefix: string;
}

export type SelectFieldOptions = { [key in keyof ProfileSelectFields]: { label: string; value: string }[] };

export type MainAccessData = { status: 'Active' | 'Expiring' | 'Expired'; type: string } | undefined;

export interface MiscProfileFields {
  passwordless: boolean;
  pianoUid: string;
  address: UserAddress | null;
  fieldOptions: SelectFieldOptions;
  mainAccessData: MainAccessData;
}

export type MyProfileData = ProfileTextFields & ProfileSelectFields & MiscProfileFields;

export type UpdatedFields = Partial<ProfileTextFields & ProfileSelectFields>;

export interface PianoAccess {
  granted: boolean;
  resource: PianoResource;
}

export interface PianoSubscription {
  end_date: number;
  status: string;
  subscription_id: string;
  start_date: number;
  term: {
    type: string;
    resource: PianoResource;
  };
}

export interface RecurlySubscription {
  uuid: string;
  created_at: string;
  current_billing_cycle: number;
  current_period_started_at: string;
  current_period_ends_at: string;
  state: 'expired' | 'active' | 'canceled';
  plan: Plan;
  ramp_intervals: {
    starting_billing_cycle: number;
  }[];
}

export interface PianoUserData {
  custom_fields: PianoCustomFieldData;
  email: string;
  first_name: string;
  last_name: string;
}

export interface SubscriptionPageData {
  status: 'Active' | 'Expiring' | 'Expired';
  type: string;
  currentPeriod?: string;
  periodEndsOn: string;
  startedOn: string;
  nextInvoice?: RenewalInvoice;
  id: string;
  inTrial: boolean;
}
